/* eslint-disable no-console */
/**
 * useImageUpload Hook
 *
 * Handles image file uploads to the server.
 * Returns permanent URLs instead of temporary blob URLs.
 * Follows Unix philosophy: one file, one job.
 *
 * @example
 * const { upload, isUploading, error } = useImageUpload({ artistId });
 *
 * const handleDrop = async (file: File) => {
 *   const url = await upload(file, 'profile_image');
 *   if (url) {
 *     // Use the permanent URL
 *   }
 * };
 */

import { useState, useCallback, useMemo } from 'react';

import { SERVER_BASE_URI } from '@pray/shared/config';

// ============================================================================
// Types
// ============================================================================

/** Supported image slot types */
export type ImageSlotType =
  | 'profile_image'
  | 'profile_vertical_image'
  | 'profile_horizontal_image'
  | 'facecoin_image'
  | 'default_cover_art_image'
  | 'pray_radio_cover_art_image'
  | 'pray_tv_cover_art_image';

export interface UseImageUploadOptions {
  /** Artist ID for the upload */
  artistId: string | null;
  /** Callback when upload succeeds */
  onSuccess?: (url: string, slotType: ImageSlotType) => void;
  /** Callback when upload fails */
  onError?: (error: Error) => void;
}

export interface UseImageUploadReturn {
  /** Upload an image file to a specific slot */
  upload: (file: File, slotType: ImageSlotType) => Promise<string | null>;
  /** Whether an upload is in progress */
  isUploading: boolean;
  /** Current upload progress (0-100) */
  progress: number;
  /** Last error from upload */
  error: Error | null;
  /** Clear any error state */
  clearError: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Validate that the file is an acceptable image type.
 */
function validateImageFile(file: File): void {
  const acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!acceptedTypes.includes(file.type)) {
    throw new Error(`Invalid file type: ${file.type}. Accepted types: ${acceptedTypes.join(', ')}`);
  }

  // Max size: 10MB
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size: 10MB`);
  }
}

/**
 * Convert a File to a data URL for fallback/preview purposes.
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file as data URL'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// Hook
// ============================================================================

export function useImageUpload({ artistId, onSuccess, onError }: UseImageUploadOptions): UseImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const upload = useCallback(
    async (file: File, slotType: ImageSlotType): Promise<string | null> => {
      if (!artistId) {
        const err = new Error('Artist ID is required for image upload');
        setError(err);
        onError?.(err);
        return null;
      }

      setError(null);
      setIsUploading(true);
      setProgress(0);

      try {
        // Validate the file
        validateImageFile(file);
        setProgress(10);

        // Create FormData for upload
        const formData = new FormData();
        formData.append('image', file);
        formData.append('slotType', slotType);

        console.log('[useImageUpload] Uploading image', {
          artistId,
          slotType,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });

        setProgress(20);

        // Upload to the server
        const response = await fetch(`${SERVER_BASE_URI}/web/studio/artists/${artistId}/images/${slotType}`, {
          method: 'PUT',
          credentials: 'include',
          body: formData,
        });

        setProgress(80);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Upload failed with status ${response.status}`);
        }

        const data = await response.json();
        const imageUrl = data.imageUrl || data.url || data.data?.imageUrl;

        if (!imageUrl) {
          throw new Error('No image URL returned from server');
        }

        setProgress(100);
        console.log('[useImageUpload] Upload successful', { imageUrl });

        onSuccess?.(imageUrl, slotType);
        return imageUrl;
      } catch (err) {
        const uploadError = err instanceof Error ? err : new Error('Upload failed');
        console.error('[useImageUpload] Upload error:', uploadError);
        setError(uploadError);
        onError?.(uploadError);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [artistId, onSuccess, onError]
  );

  return useMemo(
    () => ({
      upload,
      isUploading,
      progress,
      error,
      clearError,
    }),
    [upload, isUploading, progress, error, clearError]
  );
}

export default useImageUpload;
